import React from 'react';
import './Login.css';
import { Alert, Col, Form as ReactForm, Row } from 'react-bootstrap';
import { ErrorMessage, Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useSubmitLoginForm } from './authActions';
import { ExternalContainer } from '../common/ExternalContainer';
import { FormControlError } from '../form/FormControlError';
import { SalvButton } from '@salvapp/ui-components';
import { Turnstile } from '@marsidev/react-turnstile';
import { currentEnvironment } from '../tracking/environment';
import { useSelector } from 'react-redux';
import { AppState } from '../store/store';
import { Navigate } from 'react-router-dom';
import { InternalNavigation } from '../navigation/helper';

const validationSchema = Yup.object().shape({
  username: Yup.string().email('Invalid email').required('Required'),
  password: Yup.string().min(4, 'Too Short!').max(64, 'Too Long!').required('Required'),
});

export interface LoginFormValues {
  username: string;
  password: string;
}

const initialValues: LoginFormValues = {
  username: '',
  password: '',
};

export function Login() {
  const environment = currentEnvironment();
  const { submitLoginForm, turnstileRef } = useSubmitLoginForm();
  const tokenResponse = useSelector((state: AppState) => state.auth.tokenResponse);

  // Redirect if user is already logged in
  if (tokenResponse && tokenResponse.accessToken) {
    return <Navigate to={InternalNavigation.root} replace />;
  }

  return (
    <ExternalContainer>
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submitLoginForm}>
        {({ status, isSubmitting, errors, touched, values, handleChange }: FormikProps<LoginFormValues>) => (
          <Form noValidate>
            <div id='username-and-password' aria-label={'Login page'}>
              <ReactForm.Group as={Row} controlId='username' className='form-group'>
                <Col md={3}>
                  <ReactForm.Label column className='text-end'>
                    Email
                  </ReactForm.Label>
                </Col>
                <Col md={6}>
                  <ReactForm.Control
                    type='text'
                    name='username'
                    placeholder='Email'
                    value={values.username}
                    onChange={handleChange}
                    isInvalid={touched.username && !!errors.username}
                  />
                </Col>
                <Col md={3}>
                  <ErrorMessage name='username' render={(message) => <FormControlError error={message} />} />
                </Col>
              </ReactForm.Group>

              <ReactForm.Group as={Row} controlId='password' className='form-group'>
                <Col md={3}>
                  <ReactForm.Label column className='text-end'>
                    Password
                  </ReactForm.Label>
                </Col>
                <Col md={6}>
                  <ReactForm.Control
                    name='password'
                    type='password'
                    placeholder='Password'
                    value={values.password}
                    onChange={handleChange}
                    isInvalid={touched.password && !!errors.password}
                  />
                </Col>
                <Col md={3}>
                  <ErrorMessage name='password' render={(message) => <FormControlError error={message} />} />
                </Col>
              </ReactForm.Group>
            </div>

            {environment?.cloudFlareSiteKey && (
              <Row className='justify-content-center mb-3'>
                <Turnstile
                  siteKey={environment.cloudFlareSiteKey}
                  options={{ appearance: 'interaction-only' }}
                  ref={turnstileRef}
                  onError={() => turnstileRef.current?.reset()}
                />
              </Row>
            )}

            <ReactForm.Group as={Row} className='form-group'>
              <Col md={{ offset: 3, span: 6 }} className='text-center'>
                <SalvButton variant='primary' disabled={isSubmitting} label='Sign in' type='submit' />
              </Col>
            </ReactForm.Group>

            <Row>
              <Col md={{ offset: 3, span: 6 }}>{status && <Alert variant='danger'>{status}</Alert>}</Col>
            </Row>

            <Row>
              <Col md={{ offset: 3, span: 6 }} className='text-center'>
                <a href='/forgot-password'>Forgot password?</a>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </ExternalContainer>
  );
}

export default Login;
